import React, { useState, useEffect, useRef } from 'react';
import { Input } from './input';
import { Card, CardContent } from './card';
import { Badge } from './badge';
import { User, Mail, Phone, ChevronDown, Check, Plus } from 'lucide-react';
import { RaynetContactPerson } from '@/api/types';
import { raynetClient } from '@/api/raynetClient';
import { cn } from '@/lib/utils';

interface ContactPersonSearchProps {
  value: string;
  onChange: (value: string) => void;
  onContactSelect?: (person: RaynetContactPerson) => void;
  companyId: number;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export const ContactPersonSearch: React.FC<ContactPersonSearchProps> = ({
  value,
  onChange,
  onContactSelect,
  companyId,
  placeholder = "Vyberte kontaktní osobu nebo zadejte novou...",
  className,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [contacts, setContacts] = useState<RaynetContactPerson[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedContact, setSelectedContact] = useState<RaynetContactPerson | null>(null);
  const [searchQuery, setSearchQuery] = useState(value);
  const [initialLoad, setInitialLoad] = useState(false);
  
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<NodeJS.Timeout>();

  // Load all contacts for the company initially
  useEffect(() => {
    if (companyId && !initialLoad) {
      setInitialLoad(true);
      loadContacts();
    }
  }, [companyId, initialLoad]);

  const loadContacts = async (query?: string) => {
    if (!companyId) return;

    setIsLoading(true);
    try {
      const results = await raynetClient.getCompanyContactPersons(companyId, query);
      setContacts(results);
      
      // Show dropdown if we have results or if user is searching
      if (results.length > 0 || (query && query.length > 0)) {
        setIsOpen(true);
      }
    } catch (error) {
      console.error('Chyba při načítání kontaktních osob:', error);
      setContacts([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (searchQuery.length > 0) {
      debounceRef.current = setTimeout(() => {
        loadContacts(searchQuery);
      }, 300);
    } else if (companyId) {
      // Load all contacts when search is empty
      loadContacts();
    }

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchQuery, companyId]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchQuery(newValue);
    onChange(newValue);
    
    // Clear selection if user is typing something different
    if (selectedContact && newValue !== selectedContact.fullName) {
      setSelectedContact(null);
    }
  };

  const handleContactSelect = (contact: RaynetContactPerson) => {
    setSelectedContact(contact);
    setSearchQuery(contact.fullName);
    onChange(contact.fullName);
    setIsOpen(false);
    onContactSelect?.(contact);
    
    // Focus input after selection
    inputRef.current?.focus();
  };

  const handleInputFocus = () => {
    if (contacts.length > 0 || companyId) {
      setIsOpen(true);
      if (contacts.length === 0) {
        loadContacts();
      }
    }
  };

  const handleNewContact = () => {
    // Close dropdown and keep current input value
    setIsOpen(false);
    setSelectedContact(null);
    inputRef.current?.focus();
  };

  // Check if current input matches any existing contact
  const exactMatch = contacts.find(contact => 
    contact.fullName.toLowerCase() === searchQuery.toLowerCase()
  );

  const showNewContactOption = searchQuery.length > 0 && !exactMatch && !selectedContact;

  return (
    <div ref={searchRef} className={cn("relative w-full", className)}>
      <div className="relative">
        <Input
          ref={inputRef}
          value={searchQuery}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder={placeholder}
          disabled={disabled}
          className={cn(
            "pr-10 transition-all duration-200",
            selectedContact && "border-green-300 focus:border-green-500",
            isLoading && "opacity-70"
          )}
        />
        
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          {isLoading ? (
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-slate-400 border-t-transparent" />
          ) : selectedContact ? (
            <Check className="h-4 w-4 text-green-500" />
          ) : (
            <ChevronDown className={cn(
              "h-4 w-4 text-slate-400 transition-transform duration-200",
              isOpen && "rotate-180"
            )} />
          )}
        </div>
      </div>

      {/* Selected contact info */}
      {selectedContact && !isOpen && (
        <Card className="mt-2 border-green-200 bg-green-50">
          <CardContent className="p-3">
            <div className="flex items-start gap-3">
              <User className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium text-green-800">{selectedContact.fullName}</span>
                  <Badge variant="secondary" className="text-xs bg-green-100 text-green-700">
                    V Raynetu
                  </Badge>
                </div>
                
                {selectedContact.position && (
                  <p className="text-sm text-green-600 mb-1">{selectedContact.position}</p>
                )}
                
                <div className="flex flex-wrap gap-3 text-sm text-green-600">
                  {selectedContact.email && (
                    <div className="flex items-center gap-1">
                      <Mail className="h-3 w-3" />
                      <span>{selectedContact.email}</span>
                    </div>
                  )}
                  {selectedContact.phone && (
                    <div className="flex items-center gap-1">
                      <Phone className="h-3 w-3" />
                      <span>{selectedContact.phone}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search results dropdown */}
      {isOpen && (contacts.length > 0 || showNewContactOption) && (
        <Card className="absolute z-50 w-full mt-1 max-h-60 overflow-y-auto shadow-lg border-slate-200">
          <CardContent className="p-0">
            {/* Existing contacts */}
            {contacts.map((contact) => (
              <div
                key={contact.id}
                onClick={() => handleContactSelect(contact)}
                className="p-3 hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-b-0 transition-colors"
              >
                <div className="flex items-start gap-3">
                  <User className="h-4 w-4 text-slate-400 mt-1 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-slate-800 truncate">{contact.fullName}</span>
                      <Badge variant="outline" className="text-xs">
                        Raynet
                      </Badge>
                    </div>
                    
                    {contact.position && (
                      <p className="text-xs text-slate-500 mb-1">{contact.position}</p>
                    )}
                    
                    <div className="flex flex-wrap gap-3 text-xs text-slate-500">
                      {contact.email && (
                        <div className="flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          <span className="truncate">{contact.email}</span>
                        </div>
                      )}
                      {contact.phone && (
                        <div className="flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          <span>{contact.phone}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            
            {/* New contact option */}
            {showNewContactOption && (
              <div
                onClick={handleNewContact}
                className="p-3 hover:bg-orange-50 cursor-pointer border-t border-orange-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Plus className="h-4 w-4 text-orange-500 flex-shrink-0" />
                  <div>
                    <span className="font-medium text-orange-700">
                      Přidat nový kontakt: "{searchQuery}"
                    </span>
                    <p className="text-xs text-orange-600 mt-1">
                      Kontakt bude vytvořen jako nový v Raynetu při odeslání formuláře
                    </p>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}; 